<template>
    <div class="participant-detailed-info">
        <!-- New 3-Tab Structure with Glider -->
        <div class="tabs" v-if="trades.length">
            <input
                type="radio"
                :id="`tab-cumulativePnl-${uniqueId}`"
                value="cumulativePnl"
                v-model="activeTab" />
            <label
                class="tab"
                :for="`tab-cumulativePnl-${uniqueId}`">
                <i class="fa fa-regular fa-chart-line"></i>
                <span class="tab-label">{{ $t('cumulativePnL') || 'Cumulative PnL' }}</span>
            </label>

            <input
                type="radio"
                :id="`tab-latestTrades-${uniqueId}`"
                value="latestTrades"
                v-model="activeTab" />
            <label
                class="tab"
                :for="`tab-latestTrades-${uniqueId}`">
                <i class="fa fa-regular fa-list"></i>
                <span class="tab-label">{{ $t('seeLatestTrades') || 'Latest Trades' }}</span>
            </label>

            <input
                type="radio"
                :id="`tab-lotsByAssets-${uniqueId}`"
                value="lotsByAssets"
                v-model="activeTab" />
            <label
                class="tab"
                :for="`tab-lotsByAssets-${uniqueId}`">
                <i class="fa fa-regular fa-chart-pie"></i>
                <span class="tab-label">{{ $t('lotsByAssets') || 'Lots by Assets' }}</span>
            </label>

            <span class="glider" :style="gliderStyle"></span>
        </div>

        <transition name="slide-fade" mode="out-in">
            <!-- Tab 1: Cumulative PnL Chart -->
            <div v-if="activeTab === 'cumulativePnl' && trades.length" key="cumulative-pnl" class="chart-content">
                <apexchart
                    v-if="cumulativePnlData.length > 0"
                    style="width: 100%;"
                    height="350px"
                    type="line"
                    :options="cumulativeChartOptions"
                    :series="cumulativeChartSeries">
                </apexchart>

                <div class="indicators">
                    <div class="indicator" v-for="(indicator, index) in indicators" :key="index">
                        <p class="indicator-name">{{ $t(indicator.name) }}</p>
                        <p class="indicator-value">{{ indicator.value }}</p>
                    </div>
                </div>
            </div>

            <!-- Tab 2: Latest Trades Table -->
            <div v-else-if="activeTab === 'latestTrades' && trades.length" key="latest-trades" class="trades-content">
                <table v-if="trades.length >= 3">
                    <thead>
                        <tr>
                            <th>{{ $t('side') }}</th>
                            <th>{{ $t('asset') }}</th>
                            <th>{{ $t('openTime') }}</th>
                            <th>{{ $t('openPrice') }}</th>
                            <th>{{ $t('closeTime') }}</th>
                            <th>{{ $t('closePrice') }}</th>
                            <th class="last-header">{{ $t('netProfit') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(trade, index) in trades" :key="index">
                            <td>{{ trade.side }}</td>
                            <td>{{ trade.asset }}</td>
                            <td>{{ formatDate(trade.openTime) }}</td>
                            <td>{{ trade.entryPrice }}</td>
                            <td>{{ formatDate(trade.closeTime) }}</td>
                            <td>{{ trade.closePrice }}</td>
                            <td>{{ trade.profit }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Tab 3: Lots by Assets Table -->
            <div v-else-if="activeTab === 'lotsByAssets' && trades.length" key="lots-by-assets" class="lots-by-asset-content">
                <table>
                    <thead>
                        <tr>
                            <th class="first-header">{{ $t('asset') }}</th>
                            <th class="last-header">{{ $t('totalLots') || 'Total Lots' }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="lotsByAsset.length === 0">
                            <td colspan="2" class="no-data-message">
                                {{ $t('noDataAvailable') || 'No data available' }}
                            </td>
                        </tr>
                        <tr v-for="(lot, index) in lotsByAsset" :key="index">
                            <td>{{ lot.assetName }}</td>
                            <td>{{ (lot.totalLots || 0).toFixed(2) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <h4 v-else class="no-content-txt">{{ $t('notEnoughTrades') }}</h4>
        </transition>
    </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';

export default {
    name: 'ParticipantDetailedInfo',
    components: {
        apexchart: VueApexCharts,
    },
        props: {
        uniqueId: {
            type: String,
            required: true,
            default: 'default'
        }
    },
    data() {
        return {
            activeTab: 'cumulativePnl', // Default to Cumulative PnL tab
            chartSeries: [
                {
                    name: 'Profit',
                    data: [],
                },
            ],
            cumulativePnlData: [],
            latestProfitsData: [],
            indicators: [
                { name: 'swisetRatio', value: '0' },
                { name: 'winRate', value: '0' },
                { name: 'riskReward', value: '0' },
                { name: 'totalTrades', value: '0' },
            ],
            trades: [],
            lotsByAsset: [],
        };
    },
    computed: {
        isDarkTheme() {
            return this.$vuetify.theme.dark;
        },
        chartOptions() {
            return {
                chart: {
                    id: 'basic-line',
                    width: '100%',
                    toolbar: {
                        show: false
                    }
                },
                theme: {
                    mode: this.isDarkTheme ? 'dark' : 'light'
                },
                tooltip: {
                    theme: this.isDarkTheme ? 'dark' : 'light',
                    style: {
                        fontSize: '12px',
                        fontFamily: 'Helvetica, Arial, sans-serif'
                    },
                    x: {
                        show: true
                    },
                    y: {
                        formatter: function(val) {
                            return val.toFixed(2);
                        }
                    }
                },
                xaxis: {
                    categories: [],
                    axisBorder: {
                        show: false
                    },
                    labels: {
                        show: false,
                        style: {
                            colors: this.isDarkTheme ? '#ffffff' : '#333333'
                        }
                    }
                },
                yaxis: {
                    labels: {
                        style: {
                            colors: this.isDarkTheme ? '#ffffff' : '#333333'
                        }
                    }
                },
                colors: ['#9A9A9A'],
                grid: {
                    show: false,
                },
                markers: {
                    size: 5,
                    colors: ['#9A9A9A'],
                    strokeColors: this.isDarkTheme ? '#333333' : '#fff',
                    strokeWidth: 2,
                    hover: {
                        size: 7,
                    },
                },
            };
        },
        organizerColor() {
            if (this.$store.state.tournament?.tournamentSettings?.colors?.primary) {
                const color = this.$store.state.tournament.tournamentSettings.colors.primary;
                return color.startsWith('#') ? color : `#${color}`;
            }
            return '#009F63'; // Default green
        },
        organizerColorStyle() {
            if (this.$store.state.tournament &&
                this.$store.state.tournament.tournamentSettings &&
                this.$store.state.tournament.tournamentSettings.colors &&
                this.$store.state.tournament.tournamentSettings.colors.primary) {
                const color = this.$store.state.tournament.tournamentSettings.colors.primary;
                // Remove # if present and re-add it
                const cleanColor = color.replace('#', '');
                const fullColor = `#${cleanColor}`;
                return {
                    backgroundColor: fullColor,
                    borderColor: fullColor,
                    color: this.getContrastYIQ(cleanColor) === 'black' ? '#000' : '#fff'
                };
            }
            return {};
        },
        gliderStyle() {
            const tabOrder = ['cumulativePnl', 'latestTrades', 'lotsByAssets'];
            const tabIndex = tabOrder.indexOf(this.activeTab);
            return {
                transform: `translateX(${tabIndex * 100}%)`,
                backgroundColor: this.organizerColor
            };
        },
        toggleColorStyle() {
            if (this.$store.state.tournament && 
                this.$store.state.tournament.tournamentSettings &&
                this.$store.state.tournament.tournamentSettings.colors &&
                this.$store.state.tournament.tournamentSettings.colors.primary) {
                const color = this.$store.state.tournament.tournamentSettings.colors.primary;
                // Remove # if present and re-add it
                const cleanColor = color.replace('#', '');
                const fullColor = `#${cleanColor}`;
                return {
                    backgroundColor: fullColor
                };
            }
            return {
                backgroundColor: 'var(--v-mainGreen-base)'
            };
        },
        // Cumulative PnL Line Chart Series
        cumulativeChartSeries() {
            return [{
                name: this.$t('cumulativePnL') || 'Cumulative PnL',
                data: this.cumulativePnlData.map(item => item.pnl || 0)
            }];
        },
        // Cumulative PnL Chart Options
        cumulativeChartOptions() {
            return {
                chart: {
                    id: 'cumulative-pnl',
                    type: 'line',
                    width: '100%',
                    toolbar: {
                        show: false
                    },
                    animations: {
                        enabled: true,
                        easing: 'easeinout',
                        speed: 800
                    }
                },
                theme: {
                    mode: this.isDarkTheme ? 'dark' : 'light'
                },
                stroke: {
                    curve: 'smooth',
                    width: 3
                },
                fill: {
                    type: 'gradient',
                    gradient: {
                        shade: 'dark',
                        type: 'vertical',
                        shadeIntensity: 0.5,
                        opacityFrom: 0.3,
                        opacityTo: 0.1,
                        stops: [0, 100]
                    }
                },
                tooltip: {
                    theme: this.isDarkTheme ? 'dark' : 'light',
                    style: {
                        fontSize: '12px',
                        fontFamily: 'Helvetica, Arial, sans-serif'
                    },
                    x: {
                        formatter: (val) => {
                            const item = this.cumulativePnlData[val - 1];
                            return item ? this.formatDateShort(item.date) : val;
                        }
                    },
                    y: {
                        formatter: function(val) {
                            return `$${val.toFixed(2)}`;
                        }
                    }
                },
                xaxis: {
                    categories: this.cumulativePnlData.map(item => this.formatDateShort(item.date)),
                    title: {
                        text: this.$t('date') || 'Date',
                        style: {
                            color: this.isDarkTheme ? '#ffffff' : '#333333'
                        }
                    },
                    labels: {
                        style: {
                            colors: this.isDarkTheme ? '#ffffff' : '#333333'
                        }
                    }
                },
                yaxis: {
                    title: {
                        text: this.$t('cumulativeProfit') || 'Cumulative Profit ($)',
                        style: {
                            color: this.isDarkTheme ? '#ffffff' : '#333333'
                        }
                    },
                    labels: {
                        style: {
                            colors: this.isDarkTheme ? '#ffffff' : '#333333'
                        },
                        formatter: function(val) {
                            return `$${val.toFixed(2)}`;
                        }
                    }
                },
                colors: [this.$store.state.tournament?.tournamentSettings?.colors?.primary ? 
                    '#' + this.$store.state.tournament.tournamentSettings.colors.primary.replace('#', '') : 
                    'var(--v-mainGreen-base)'],
                grid: {
                    borderColor: this.isDarkTheme ? '#444' : '#e0e0e0',
                    strokeDashArray: 5,
                    row: {
                        colors: this.isDarkTheme ? ['#2a2a2a', 'transparent'] : ['#f3f3f3', 'transparent'],
                        opacity: 0.5
                    }
                },
                markers: {
                    size: 6,
                    colors: [this.$store.state.tournament?.tournamentSettings?.colors?.primary ? 
                        '#' + this.$store.state.tournament.tournamentSettings.colors.primary.replace('#', '') : 
                        'var(--v-mainGreen-base)'],
                    strokeColors: this.isDarkTheme ? '#333333' : '#fff',
                    strokeWidth: 2,
                    hover: {
                        size: 8,
                    }
                }
            };
        },
        // Latest Trades Bar Chart Series
        latestTradesChartSeries() {
            return [{
                name: this.$t('tradeProfit') || 'Trade Profit',
                data: this.latestProfitsData.map(item => item.profit || 0)
            }];
        },
        // Latest Trades Bar Chart Options
        latestTradesChartOptions() {
            return {
                chart: {
                    id: 'latest-trades',
                    type: 'bar',
                    width: '100%',
                    toolbar: {
                        show: false
                    },
                    animations: {
                        enabled: true,
                        easing: 'easeinout',
                        speed: 800
                    }
                },
                theme: {
                    mode: this.isDarkTheme ? 'dark' : 'light'
                },
                plotOptions: {
                    bar: {
                        borderRadius: 6,
                        columnWidth: '60%',
                        distributed: false,
                        colors: {
                            ranges: this.latestProfitsData.map(item => {
                                const value = item.profit || 0;
                                return {
                                    from: value < 0 ? value : 0,
                                    to: value < 0 ? 0 : value,
                                    color: value < 0 ? '#EF5350' :
                                        (this.$store.state.tournament?.tournamentSettings?.colors?.primary ?
                                            '#' + this.$store.state.tournament.tournamentSettings.colors.primary.replace('#', '') :
                                            'var(--v-mainGreen-base)')
                                };
                            })
                        }
                    }
                },
                dataLabels: {
                    enabled: false
                },
                tooltip: {
                    theme: this.isDarkTheme ? 'dark' : 'light',
                    style: {
                        fontSize: '12px',
                        fontFamily: 'Helvetica, Arial, sans-serif'
                    },
                    x: {
                        formatter: (val, opts) => {
                            const index = opts.dataPointIndex;
                            const item = this.latestProfitsData[index];
                            return item ? this.formatDateShort(item.date) : val;
                        }
                    },
                    y: {
                        formatter: function(val) {
                            return `$${val.toFixed(2)}`;
                        }
                    }
                },
                xaxis: {
                    categories: this.latestProfitsData.map(item => this.formatDateShort(item.date)),
                    title: {
                        text: this.$t('date') || 'Date',
                        style: {
                            color: this.isDarkTheme ? '#ffffff' : '#333333'
                        }
                    },
                    labels: {
                        style: {
                            colors: this.isDarkTheme ? '#ffffff' : '#333333'
                        },
                        rotate: -45,
                        rotateAlways: false
                    }
                },
                yaxis: {
                    title: {
                        text: this.$t('profit') || 'Profit ($)',
                        style: {
                            color: this.isDarkTheme ? '#ffffff' : '#333333'
                        }
                    },
                    labels: {
                        style: {
                            colors: this.isDarkTheme ? '#ffffff' : '#333333'
                        },
                        formatter: function(val) {
                            return `$${val.toFixed(2)}`;
                        }
                    }
                },
                grid: {
                    borderColor: this.isDarkTheme ? '#444' : '#e0e0e0',
                    strokeDashArray: 5,
                    xaxis: {
                        lines: {
                            show: true
                        }
                    },
                    yaxis: {
                        lines: {
                            show: true
                        }
                    }
                }
            };
        }
    },
    watch: {
        "$store.state.currentDetailedInfo": {
            immediate: true,
            handler(newVal) {
                this.updateDetailedInfo(newVal);
            }
        }
    },
    methods: {
        updateDetailedInfo(info) {
            // eslint-disable-next-line no-console
            console.log("info", info);
            if(info && info.latestTrades && info.latestTrades.length){
                this.indicators = [
                    { name: 'swisetRatio', value: info?.swisetRatio?.toFixed(2) },
                    { name: 'winRate', value: `${(info?.winRate * 100)?.toFixed(2)}%` },
                    { name: 'riskReward', value: info?.riskRewardRatio?.toFixed(2) },
                    { name: 'totalTrades', value: info?.totalTrades },
                ];
                this.trades = info.latestTrades.slice(0, 7);
                this.lotsByAsset = info.lotsByAsset || [];

                // Set new chart data from backend
                this.cumulativePnlData = info.cumulativePnl || [];
                this.latestProfitsData = info.latestProfits || [];

                // Fallback: Use original chart data if new data not available
                if (!this.cumulativePnlData.length && !this.latestProfitsData.length) {
                    const reversedTrades = info.latestTrades.slice(0,7).reverse();
                    this.chartSeries[0].data = reversedTrades.map(trade => trade.profit);
                }
            }
        },
        formatDate(dateString) {
            const date = new Date(dateString);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
        },
        formatDateShort(dateString) {
            const date = new Date(dateString);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            return `${month}/${day}`;
        },
        getContrastYIQ(hexcolor) {
            if (!hexcolor) return 'black';
            
            hexcolor = hexcolor.replace('#', '');
            
            const r = parseInt(hexcolor.substr(0, 2), 16);
            const g = parseInt(hexcolor.substr(2, 2), 16);
            const b = parseInt(hexcolor.substr(4, 2), 16);
            
            const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
            
            return (yiq >= 128) ? 'black' : 'white';
        }
    }
};
</script>

<style scoped lang="scss">
.participant-detailed-info {
    width: 100%;
    background-color: var(--v-fullWhite-base);
    font-family: "Avenir", sans-serif;
    .no-content-txt{
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 32px !important;
        font-family: "Avenir", sans-serif !important;
        text-align: center;
        height: 250px;
        color: var(--v-mainBlack-base);
        opacity: 0.2;
        @media screen and (max-width: 768px) {
            font-size: 18px;
        }
    }
}

.tabs {
    display: flex;
    position: relative;
    background-color: var(--v-progressBarBackground-base);
    border-radius: 10px;
    width: 100%;
    margin-bottom: 10px;

    * {
        z-index: 2;
    }

    input[type="radio"] {
        display: none;

        &:checked {
            & + label {
                color: white !important;

                i {
                    color: white !important;
                }
            }
        }
    }

    .tab {
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 1;
        height: 38px;
        font-size: 14px;
        font-weight: 600;
        color: var(--v-mainBlack-base);
        border-radius: 10px;
        cursor: pointer;
        transition: color 0.15s ease-in;
        font-family: "Avenir", sans-serif;
        padding: 0 16px;
        gap: 8px;

        i {
            font-size: 14px;
            flex-shrink: 0;
        }

        .tab-label {
            line-height: 1.4;
        }

        // Mobile responsive
        @media screen and (max-width: 768px) {
            font-size: 12px;
            padding: 0 8px;
            gap: 6px;

            i {
                font-size: 12px;
            }

            .tab-label {
                display: none;
            }
        }

        @media screen and (max-width: 480px) {
            font-size: 11px;
            padding: 0 6px;

            i {
                font-size: 16px;
            }
        }
    }

    .glider {
        position: absolute;
        display: flex;
        height: 38px;
        width: calc(100% / 3); // For 3 tabs
        border-radius: 10px;
        z-index: 1;
        transition: 0.25s ease-out;
    }
}

.theme--dark .tabs {
    background-color: #303740;

    .tab {
        color: var(--v-fullWhite-base);

        &:hover {
            opacity: 0.8;
        }
    }
}


.chart-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
}

.indicators {
    display: flex;
    justify-content: space-around;
    align-items: center;
    margin-top: 20px;
    width: 100%;
}

.indicator {
    display: flex;
    justify-content: space-between;
    color: #fff;
    padding: 10px;
    align-items: center;
    border-radius: 10px;
    border: 1px solid var(--v-mainBlack-base);
    text-align: center;
    font-size: 12px;

    .indicator-value {
        font-weight: bold;
        margin-left: 40px;
        font-size: 14px;
    }
}

.trades-content {
    width: 100%;

    table {
        width: 100%;
        border-collapse: collapse;

        th,
        td {
            padding: 20px 10px;
            text-align: center;
            border-bottom: 1px solid #eee;
        }

        th {
            background-color: var(--v-progressBarBackground-base);
            ;
            border-bottom: none;
        }

        .first-header {
            border-top-left-radius: 10px;
            border-bottom-left-radius: 10px;
        }

        .last-header {
            border-top-right-radius: 10px;
            border-bottom-right-radius: 10px;
        }

        .no-data-message {
            padding: 40px 20px;
            color: var(--v-mainBlack-base);
            opacity: 0.6;
            font-style: italic;
        }
    }
}


.lots-by-asset-content {
    width: 100%;

    table {
        width: 100%;
        border-collapse: collapse;
        table-layout: fixed;

        th,
        td {
            padding: 20px 10px;
            text-align: center;
            border-bottom: 1px solid #eee;
        }

        th {
            background-color: var(--v-progressBarBackground-base);
            border-bottom: none;
        }

        th:first-child,
        td:first-child {
            width: 50%;
        }

        th:last-child,
        td:last-child {
            width: 50%;
        }

        tbody tr {
            transition: background-color 0.2s ease;

            &:nth-child(even) {
                background-color: #f9f9f9;
            }

            &:hover {
                background-color: #f0f0f0;
            }
        }

        .theme--dark & tbody tr {
            &:nth-child(even) {
                background-color: rgba(255, 255, 255, 0.03);
            }

            &:hover {
                background-color: rgba(255, 255, 255, 0.08);
            }
        }

        .first-header {
            border-top-left-radius: 10px;
            border-bottom-left-radius: 10px;
        }

        .last-header {
            border-top-right-radius: 10px;
            border-bottom-right-radius: 10px;
        }

        .no-data-message {
            padding: 40px 20px;
            color: var(--v-mainBlack-base);
            opacity: 0.6;
            font-style: italic;
        }
    }
}

@media screen and (max-width: 768px) {
    .tabs {
        flex-direction: column;
        align-items: stretch;
    }

    .tabs-container {
        flex-direction: column;
    }

    .tabs button {
        width: 100%;
    }
}

/* Slide effect for tab transition */
.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all 0.3s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
    transform: translateX(10px);
    opacity: 0;
}
::v-deep .apexcharts-tooltip.apexcharts-theme-dark .apexcharts-tooltip-title {
   background-color: transparent;
}

::v-deep .apexcharts-xaxistooltip.apexcharts-theme-dark{
    background-color: transparent;
}
</style>
